<div class="pm-shell scrum-page">
  <div class="page-head">
    <h1>Scrum</h1>
    <p class="subtitle">Backlog, sprint planning, and issue board (To Do → In Progress → Blocked → Done).</p>
  </div>

  @if (error()) {
    <div class="error-banner">
      <span>{{ error() }}</span>
      <button mat-icon-button type="button" (click)="error.set(null)"><mat-icon>close</mat-icon></button>
    </div>
  }

  <div class="pm-toolbar scrum-toolbar">
    <mat-form-field appearance="outline" class="toolbar-field">
      <mat-label>Project</mat-label>
      <mat-select
        [value]="selectedProjectId()"
        (selectionChange)="selectProject($event.value)">
        @for (p of projects(); track p.id) {
          <mat-option [value]="p.id">{{ p.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="toolbar-field">
      <mat-label>Sprint</mat-label>
      <mat-select
        [value]="selectedSprintId()"
        (selectionChange)="selectSprint($event.value)"
        [disabled]="!sprints().length">
        @if (!sprints().length) {
          <mat-option disabled value="">No sprints yet</mat-option>
        }
        @for (s of sprints(); track s.id) {
          <mat-option [value]="s.id">{{ s.name }} ({{ statusLabels[s.status] || s.statusLabel }})</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (selectedSprint(); as sprint) {
      <span class="sprint-meta">
        {{ sprint.issueCount }} issues · {{ sprint.doneIssueCount }} done
        @if (sprint.totalStoryPoints != null || sprint.committedPointsAtStart != null) {
          · {{ sprint.doneStoryPoints || 0 }}/{{ committedPoints(sprint) }} pts
        }
        @if (sprint.capacityPoints != null || editCapacityPoints != null) {
          · capacity {{ editCapacityPoints ?? sprint.capacityPoints ?? '—' }} pts
        }
      </span>
      <span class="health-chip" [attr.data-health]="sprintHealthLabel()">
        Health: {{ sprintHealthLabel() }} · Overdue {{ overdueCount() }} · {{ completionPercent() }}%
      </span>

      @if (canStart()) {
        <button mat-flat-button color="primary" type="button" (click)="startSprint()">
          <mat-icon>play_arrow</mat-icon> Start sprint
        </button>
      }
      @if (canComplete()) {
        <button mat-stroked-button color="primary" type="button" (click)="completeSprint()">
          <mat-icon>check_circle</mat-icon> Complete
        </button>
        <button mat-stroked-button type="button" (click)="cancelSprint()">
          <mat-icon>cancel</mat-icon> Cancel
        </button>
      }
    }

    @if (loading()) {
      <mat-spinner diameter="28"></mat-spinner>
    }
  </div>

  @if (showCompleteDialog) {
    <div class="complete-dialog" role="dialog" aria-modal="true">
      <div class="complete-dialog-card">
        <h3>Complete sprint</h3>
        <p class="muted">Choose where incomplete issues should go. Capacity on the target sprint is left unchanged.</p>
        <label class="radio-row">
          <input type="radio" name="completeDest" [(ngModel)]="completeDestination" value="backlog" />
          Move incomplete to backlog
        </label>
        <label class="radio-row">
          <input type="radio" name="completeDest" [(ngModel)]="completeDestination" value="sprint" />
          Carry forward to next planned sprint
        </label>
        @if (completeDestination === 'sprint') {
          <mat-form-field appearance="outline" class="full">
            <mat-label>Target sprint</mat-label>
            <mat-select [(ngModel)]="carryToSprintId">
              @for (s of carryForwardCandidates(); track s.id) {
                <mat-option [value]="s.id">{{ s.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          @if (!carryForwardCandidates().length) {
            <p class="muted">No planned sprints available. Create one first, or move to backlog.</p>
          }
        }
        <div class="dialog-actions">
          <button mat-button type="button" (click)="cancelCompleteDialog()">Cancel</button>
          <button mat-flat-button color="primary" type="button" (click)="confirmCompleteSprint()">Complete sprint</button>
        </div>
      </div>
    </div>
  }

  <div class="layout-2 scrum-layout">
    <aside class="side-panel">
      <h3>New sprint</h3>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Name</mat-label>
        <input matInput [(ngModel)]="newSprintName" placeholder="Sprint 1" />
      </mat-form-field>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Goal</mat-label>
        <input matInput [(ngModel)]="newSprintGoal" />
      </mat-form-field>
      <div class="date-row">
        <mat-form-field appearance="outline" class="full">
          <mat-label>Start</mat-label>
          <input matInput type="date" [(ngModel)]="newSprintStart" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full">
          <mat-label>End</mat-label>
          <input matInput type="date" [(ngModel)]="newSprintEnd" />
        </mat-form-field>
      </div>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Capacity (points)</mat-label>
        <input matInput type="number" [(ngModel)]="newSprintCapacityPoints" min="0" step="0.5" />
      </mat-form-field>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Capacity (hours)</mat-label>
        <input matInput type="number" [(ngModel)]="newSprintCapacityHours" min="0" step="0.5" />
      </mat-form-field>
      <button mat-flat-button color="primary" type="button" class="full-btn" (click)="createSprint()">
        <mat-icon>add</mat-icon>
        Create sprint
      </button>

      @if (selectedSprint(); as sprint) {
        <h3 class="backlog-title">Sprint capacity</h3>
        <mat-form-field appearance="outline" class="full">
          <mat-label>Capacity points</mat-label>
          <input matInput type="number" [(ngModel)]="editCapacityPoints" min="0" step="0.5" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full">
          <mat-label>Capacity hours</mat-label>
          <input matInput type="number" [(ngModel)]="editCapacityHours" min="0" step="0.5" />
        </mat-form-field>
        <button mat-stroked-button type="button" class="full-btn" (click)="saveSprintCapacity()">
          Save capacity
        </button>
        <p class="hint capacity-hint">
          Committed: {{ committedPoints(sprint) }} pts
          @if (editCapacityPoints != null) {
            · {{ committedPoints(sprint) <= editCapacityPoints ? 'within' : 'over' }} capacity
          }
        </p>
      }

      <h3 class="backlog-title">Product backlog</h3>
      <div class="backlog-list">
        @for (issue of backlog(); track issue.id) {
          <div class="task-card static" (click)="openIssueDrawer(issue, $event)">
            <button type="button" class="key key-btn" (click)="openIssueDrawer(issue, $event)">{{ issue.key }}</button>
            <strong>{{ issue.summary }}</strong>
            <div class="meta">
              <span class="chip">{{ issue.typeLabel }}</span>
              <span class="tt-badge" [ngClass]="priorityBadgeClass(issue.priority)">{{ issue.priorityLabel }}</span>
              @if (issue.storyPoints != null) {
                <span class="chip">{{ issue.storyPoints }} pts</span>
              }
            </div>
            <button mat-stroked-button type="button" class="add-btn"
                    [disabled]="!selectedSprintId() || selectedSprint()?.status === 2 || selectedSprint()?.status === 3"
                    (click)="addToSprint(issue); $event.stopPropagation()">
              Add to sprint
            </button>
          </div>
        } @empty {
          <p class="empty">No backlog issues. Create issues first.</p>
        }
      </div>
    </aside>

    <section class="board-area">
      @if (!selectedProjectId()) {
        <p class="empty board-empty">Select a project to open the scrum board.</p>
      } @else if (!selectedSprintId()) {
        <div class="board-empty-card">
          <h3>No sprint selected</h3>
          <p>Create a sprint on the left, then add backlog issues to populate the board.</p>
        </div>
        <div class="board-scroll" cdkDropListGroup>
          @for (col of columns(); track col.key) {
            <div class="board-column">
              <h3>
                <span>{{ col.label }}</span>
                <span class="count">0</span>
              </h3>
              <div class="drop-list">
                <p class="empty">Waiting for sprint</p>
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="board-scroll" cdkDropListGroup>
          @for (col of columns(); track col.key) {
            <div class="board-column">
              <h3>
                <span>{{ col.label }}</span>
                <span class="count">{{ col.issues.length }}</span>
              </h3>
              <div
                class="drop-list"
                cdkDropList
                [id]="col.key"
                [cdkDropListData]="col.issues"
                [cdkDropListConnectedTo]="connectedLists()"
                [cdkDropListDisabled]="!canMoveBoard()"
                (cdkDropListDropped)="drop($event, col)">
                @for (issue of col.issues; track issue.id) {
                  <div class="task-card" [class.overdue]="issue.isOverdue" cdkDrag [cdkDragDisabled]="!canMoveBoard()" (click)="openIssueDrawer(issue, $event)">
                    <div class="card-top">
                      <button type="button" class="key key-btn" (click)="openIssueDrawer(issue, $event)">{{ issue.key }}</button>
                      @if (issue.isOverdue) {
                        <span class="tt-badge tt-badge--overdue">Overdue</span>
                      }
                    </div>
                    <strong>{{ issue.summary }}</strong>
                    @if (issue.isOverdue) {
                      <div class="overdue-banner">{{ issue.delayLabel || ('Overdue by ' + (issue.delayInDays || 0) + ' days') }}</div>
                    } @else if (issue.deadlineBadge || issue.remainingLabel) {
                      <div class="remaining deadline-badge" [ngClass]="deadlineBadgeClass(issue.deadlineBadge, issue.isOverdue)">{{ issue.deadlineBadge || issueDeadlineLabel(issue) }}</div>
                    }
                    <div class="meta">
                      <span class="chip">{{ issue.statusLabel }}</span>
                      <span class="tt-badge" [ngClass]="priorityBadgeClass(issue.priority)">{{ issue.priorityLabel }}</span>
                      @if (issue.assigneeName) {
                        <span class="chip">{{ issue.assigneeName }}</span>
                      }
                    </div>
                    @if (selectedSprint()?.status !== 2 && selectedSprint()?.status !== 3) {
                      <button mat-button type="button" class="remove-btn" (click)="removeFromSprint(issue); $event.stopPropagation()">
                        To backlog
                      </button>
                    }
                  </div>
                } @empty {
                  <p class="empty">{{ canMoveBoard() ? 'Drop issues here' : 'No issues' }}</p>
                }
              </div>
            </div>
          }
        </div>

        @if (canStart()) {
          <p class="hint board-hint">
            Sprint is planned. Add issues from the backlog, then click <strong>Start sprint</strong> when ready.
            Drag-and-drop works for planning and while the sprint is active.
          </p>
        }

        @if (selectedSprint(); as sprint) {
          @if (sprint.status === 2 || sprint.status === 1) {
            <div class="retro-panel">
              <h3>Sprint retrospective</h3>
              <p class="muted">
                Committed {{ committedPoints(sprint) }} pts · Completed {{ sprint.doneStoryPoints || 0 }} pts
              </p>
              <mat-form-field appearance="outline" class="full">
                <mat-label>What went well</mat-label>
                <textarea matInput rows="3" [(ngModel)]="retroWhatWentWell"></textarea>
              </mat-form-field>
              <mat-form-field appearance="outline" class="full">
                <mat-label>What to improve</mat-label>
                <textarea matInput rows="3" [(ngModel)]="retroWhatToImprove"></textarea>
              </mat-form-field>
              <mat-form-field appearance="outline" class="full">
                <mat-label>Action items</mat-label>
                <textarea matInput rows="3" [(ngModel)]="retroActionItems"></textarea>
              </mat-form-field>
              <button mat-flat-button color="primary" type="button" [disabled]="savingRetro()" (click)="saveRetrospective()">
                {{ savingRetro() ? 'Saving…' : 'Save retrospective' }}
              </button>
            </div>
          }
        }
      }
    </section>
  </div>
</div>
